// src/pages/Users/Inscription/AdherentCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import ValidateTrialModal from "./ValidateTrialModal";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSettings } from "../../../services/settingsServices";
import {
  getReglementInscriptionByIdAndSaison,
  updateReglementInscription,
} from "../../../services/reglementinscriptionServices";
import {
  FaUserCircle,
  FaUsers,
  FaFileMedical,
  FaFileSignature,
  FaEuroSign,
  FaGoogleDrive,
  FaCalendar,
  FaBell,
  FaFilePdf,
  FaThumbsUp,
  FaCheckCircle,
  FaTimesCircle,
  FaLink,
  FaCalendarAlt,
  FaPlusCircle,
} from "react-icons/fa";
import { sendNotificationToAdmin } from "./../../../services/notificationServices";
import EditDocumentsModal from "./EditDocumentsModal";

/* ---------- Helpers d’URL / icônes doc ---------- */
const toPublicUrl = (url) =>
  url && url.startsWith("/uploads/")
    ? `${process.env.REACT_APP_API_URL}${url}`
    : url;

const isDrive = (u) =>
  !!u &&
  (u.includes("drive.google.com") ||
    u.includes("docs.google.com") ||
    u.includes("googleusercontent.com"));

const isImage = (u) =>
  !!u && /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((u || "").split("?")[0]);

const isPdf = (u) =>
  !!u && (u.toLowerCase().endsWith(".pdf") || u.toLowerCase().includes("pdf"));

/** Grosse tuile icône document cliquable (ouvre si présent) */
const BigDocButton = ({ url, onOpen, label }) => {
  const has = !!url;
  let Icon = FaTimesCircle;
  let color = "text-slate-300";
  let sub = "Aucun document";

  if (has) {
    if (isDrive(url)) {
      Icon = FaGoogleDrive;
      color = "text-sky-600";
      sub = "Lien Google Drive";
    } else if (isPdf(url)) {
      Icon = FaFilePdf;
      color = "text-violet-600";
      sub = "Fichier PDF";
    } else if (isImage(url)) {
      Icon = FaFilePdf; // image -> icône fichier générique; change en FaImage si tu préfères
      color = "text-emerald-600";
      sub = "Image";
    } else {
      Icon = FaLink;
      color = "text-slate-600";
      sub = "Lien";
    }
  }

  return (
    <button
      type="button"
      onClick={has ? onOpen : undefined}
      className={`group w-full flex flex-col items-center justify-center rounded-xl border border-dashed p-4 transition
        ${
          has
            ? "border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            : "border-slate-300 bg-slate-50/50 dark:bg-slate-900/30 cursor-not-allowed"
        }`}
      title={has ? "Ouvrir le document" : "Aucun document"}
    >
      <Icon className={`text-4xl mb-1.5 ${color} ${has ? "group-hover:scale-105" : ""}`} />
      <div className="text-xs font-medium text-slate-700 dark:text-slate-200">{label}</div>
      <div className={`text-[11px] ${has ? "text-slate-500" : "text-slate-400"}`}>
        {sub} {has && "— cliquer pour ouvrir"}
      </div>
    </button>
  );
};

/* ---------- Section card (fond vert si validé, gris sinon) ---------- */
const Section = ({ title, icon, validated, children }) => {
  const bg = validated ? "bg-emerald-50 dark:bg-emerald-900/20" : "bg-slate-50 dark:bg-slate-900/40";
  const ring = validated ? "ring-emerald-200/70 dark:ring-emerald-800/40" : "ring-slate-200/70 dark:ring-slate-800/70";
  return (
    <div className={`rounded-2xl p-4 ${bg} ring-1 ${ring}`}>
      <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
        <span className="text-base">{icon}</span>
        <h4 className="font-semibold text-sm sm:text-base">{title}</h4>
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
};

const AdherentCard = ({
  adherent,
  dossier,
  user,
  refreshAdherents,
  refreshDossier,
}) => {
  const navigate = useNavigate();

  const [activeSeason, setActiveSeason] = useState("");
  const [settings, setSettings] = useState(null);
  const [reglement, setReglement] = useState(null);
  const [isTrialOpen, setIsTrialOpen] = useState(false);
  const [isDocsOpen, setIsDocsOpen] = useState(false);

  const [editingType, setEditingType] = useState(false);
  const [typeReglementLocal, setTypeReglementLocal] = useState("");
  const [savingType, setSavingType] = useState(false);

  const defaultImageUrl =
    adherent?.genre === "Masculin"
      ? "/img/judoka_avatar.png"
      : "/img/judokate_avatar.png";

  /* ---------- Settings + Saison ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data[0]) {
          setSettings(data[0]);
          setActiveSeason(data[0].saison_active || "");
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  /* ---------- Règlement courant ---------- */
  const refetchReglement = async () => {
    if (!activeSeason) return;
    try {
      const data = await getReglementInscriptionByIdAndSaison(
        adherent.id,
        activeSeason
      );
      setReglement(data || null);
    } catch (e) {
      console.error("Refetch reglement failed:", e);
    }
  };

  useEffect(() => {
    (async () => {
      if (!activeSeason) return;
      try {
        const data = await getReglementInscriptionByIdAndSaison(
          adherent.id,
          activeSeason
        );
        setReglement(data || null);
      } catch (e) {
        console.error("Erreur lors de la récupération des règlements:", e);
      }
    })();
  }, [adherent.id, activeSeason]);

  useEffect(() => {
    setTypeReglementLocal(reglement?.type_reglement || "");
  }, [reglement]);

  /* ---------- Helpers état dossier ---------- */
  if (!adherent) return null;
  if (!dossier)
    return (
      <div className="rounded-2xl ring-1 ring-slate-200 dark:ring-slate-700 p-4 bg-white/80 dark:bg-slate-900/60">
        Pas de dossier d’inscription pour {adherent.nom} {adherent.prenom}.
      </div>
    );

  const licenceSignerUrl = dossier.formulaire_licence_a_signer || "";
  const licenceSigneeUrl =
    dossier.formulaire_licence_signee || dossier.formulaire_licence_signe || "";
  const licenceAssuranceUrl =
    dossier.licence_assurance_url ||
    dossier.licence_assurance ||
    dossier.licence_url ||
    "";

  const docsProvided =
    (dossier.cm_url ? 1 : 0) +
    (licenceSigneeUrl ? 1 : 0) +
    (licenceAssuranceUrl ? 1 : 0);

  /* ---------- Badges en-tête ---------- */
  const getStatusPillClass = (statut) => {
    switch (statut) {
      case "Licencié":
        return "bg-green-300 text-slate-800 ring-1 ring-green-300/70";
      case "Licencié Hors Club":
        return "bg-green-200 text-slate-800 ring-1 ring-green-200/70";
      case "Inscrit":
        return "bg-yellow-300 text-slate-900 ring-1 ring-yellow-300/70";
      case "Cours d'essai":
        return "bg-red-300 text-slate-900 ring-1 ring-red-300/70";
      case "Licencié - Arrêt":
      case "Ancien Licencié et Arrêt":
        return "bg-slate-300 text-slate-800 ring-1 ring-slate-300/70";
      case "Ancien Licencié":
        return "bg-slate-900 text-white ring-1 ring-slate-900/50";
      case "Non Licencié - Arrêt":
        return "bg-slate-500 text-white ring-1 ring-slate-500/70";
      default:
        return "bg-slate-200 text-slate-800 ring-1 ring-slate-200/70";
    }
  };

  const docsBadgeClass =
    docsProvided >= 3
      ? "bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300"
      : docsProvided > 0
      ? "bg-amber-200 text-amber-900 ring-1 ring-amber-300"
      : "bg-slate-200 text-slate-800 ring-1 ring-slate-300";

  /* ---------- Progress (validation par le bureau) ---------- */
  const steps = [
    { key: "validate_cm", label: "Médical", done: !!dossier.validate_cm, icon: FaFileMedical },
    { key: "validate_licence", label: "Licence", done: !!dossier.validate_licence, icon: FaFileSignature },
    { key: "validate_reglement", label: "Règlement", done: !!dossier.validate_reglement, icon: FaEuroSign },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const percent = Math.round((doneCount / steps.length) * 100);

  /* ---------- Refresh dossier ---------- */
  const handleRefreshDossier = async () => {
    if (typeof refreshDossier === "function") {
      await refreshDossier(adherent.id);
    } else {
      await refreshAdherents?.();
    }
    await refetchReglement();
  };

  /* ---------- Règlement: changer type ---------- */
  const onSaveType = async () => {
    if (!reglement?.id) return;
    try {
      setSavingType(true);
      await updateReglementInscription({
        id: reglement.id,
        type_reglement: typeReglementLocal || null,
      });
      await refetchReglement();
      await handleRefreshDossier();
      setEditingType(false);
      toast.success("Type de règlement mis à jour.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la mise à jour du type de règlement.");
    } finally {
      setSavingType(false);
    }
  };

  const openUrl = (u) => {
    if (!u) return;
    const href = toPublicUrl(u);
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    if (!s.startsWith("/uploads")) return s;
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const statusChip = (status) => {
    const map = {
      "Non Réglé": "bg-rose-600 text-white",
      Réglé: "bg-emerald-600 text-white",
      "En cours": "bg-yellow-500 text-black",
      "En Attente": "bg-amber-300 text-slate-900",
      Annulé: "bg-slate-900 text-white",
      Remboursé: "bg-violet-600 text-white",
      "Partiellement Réglé": "bg-emerald-200 text-slate-900",
    };
    return map[status] || "bg-slate-200 text-slate-900";
  };

  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/70">
      {/* Header */}
      <div className="p-4 sm:p-5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/40 dark:to-slate-900/40">
        <div className="flex items-center gap-4">
          <img
            src={toAbsolute(adherent.photo_url) || defaultImageUrl}
            alt={`${adherent.nom} ${adherent.prenom}`}
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
          />
          <div className="min-w-0">
            <div className="text-lg sm:text-xl font-semibold text-slate-900 dark:text-slate-100 truncate">
              {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
            </div>

            {/* Badges */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusPillClass(adherent.statut_inscription)}`}>
                {adherent.statut_inscription || "—"}
              </span>
              <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-200 text-emerald-900 ring-1 ring-emerald-300">
                <FaCalendar className="mr-1" />
                {adherent.saison_active || "—"}
              </span>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${docsBadgeClass}`}>
                Docs : {docsProvided}/3
              </span>
              <button
                type="button"
                onClick={() => setIsDocsOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                title="Ajouter / Modifier des documents"
              >
                <FaPlusCircle />
                Ajouter / Modifier des documents
              </button>
            </div>

            {/* Méta */}
            <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs sm:text-sm text-slate-600 dark:text-slate-300">
              <div className="flex items-center gap-2">
                <FaUserCircle />
                Licence : {adherent.licence_number || "—"}
              </div>
              <div className="flex items-center gap-2">
                <FaUsers />
                Inscrit(e) le : {adherent.createdAt ? new Date(adherent.createdAt).toLocaleDateString() : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Barre de progression (validation du bureau) */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300">
            <span>Avancement (validation du bureau)</span>
            <span>{percent}%</span>
          </div>
          <div className="mt-1 h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500" style={{ width: `${percent}%` }} />
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.key} className="flex items-center gap-1.5">
                  <Icon className={s.done ? "text-green-500" : "text-slate-400"} />
                  <span>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Les documents sont déposés par vos soins. La validation est effectuée par le bureau.
        </p>
      </div>

      {/* Sections */}
      <div className="p-4 sm:p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Médical */}
          <Section title="Partie médicale" icon={<FaFileMedical />} validated={!!dossier.validate_cm}>
            <div className="grid grid-cols-1 gap-3">
              <BigDocButton
                url={dossier.cm_url}
                onOpen={() => openUrl(dossier.cm_url)}
                label="Certificat / Attestation"
              />

              <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                <FaCalendarAlt className={dossier.date_cm ? "text-emerald-600" : "text-slate-400"} />
                {dossier.date_cm ? (
                  <span>Dernier certificat : {new Date(dossier.date_cm).toLocaleDateString()}</span>
                ) : (
                  <span className="text-slate-500">Date du certificat non renseignée</span>
                )}
              </div>
            </div>
          </Section>

          {/* Licence & Assurance */}
          <Section title="Licence & Assurance" icon={<FaFileSignature />} validated={!!dossier.validate_licence}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <BigDocButton
                url={licenceSigneeUrl}
                onOpen={() => openUrl(licenceSigneeUrl)}
                label="Formulaire signé"
              />
              <BigDocButton
                url={licenceAssuranceUrl}
                onOpen={() => openUrl(licenceAssuranceUrl)}
                label="Licence / Assurance"
              />
            </div>

            {licenceSignerUrl && (
              <div className="mt-3 text-xs text-slate-600 dark:text-slate-300">
                Lien “formulaire vierge à signer” fourni par le club :
                {" "}
                <a
                  href={toPublicUrl(licenceSignerUrl)}
                  className="underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  ouvrir
                </a>
              </div>
            )}
          </Section>

          {/* Règlement */}
          <Section title="Règlement" icon={<FaEuroSign />} validated={!!dossier.validate_reglement}>
            <div className="text-sm text-slate-700 dark:text-slate-300">
              {reglement ? (
                <>
                  <div className="flex items-center gap-2">
                    <span>Statut :</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusChip(
                        reglement.statut_reglement || ""
                      )}`}
                    >
                      {reglement.statut_reglement || "—"}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mt-1">
                    <span>Type :</span>
                    {!editingType ? (
                      <>
                        <span className="font-medium">{reglement?.type_reglement || "—"}</span>
                        {reglement && (
                          <button
                            type="button"
                            onClick={() => {
                              setTypeReglementLocal(reglement?.type_reglement || "");
                              setEditingType(true);
                            }}
                            className="ml-2 inline-flex items-center rounded-lg px-2 py-1 text-xs font-medium ring-1 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                          >
                            Modifier
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="mt-1 flex items-center gap-2">
                        <select
                          value={typeReglementLocal}
                          onChange={(e) => setTypeReglementLocal(e.target.value)}
                          disabled={savingType}
                          className="rounded-lg border border-slate-300 dark:border-slate-700 px-2 py-1 text-sm bg-white dark:bg-slate-900"
                        >
                          <option value="">— Sélectionner —</option>
                          <option value="En ligne">En ligne</option>
                          <option value="Chèque">Chèque</option>
                          <option value="Espèces">Espèces</option>
                          <option value="Virement">Virement</option>
                          <option value="CB">CB</option>
                          <option value="Autre">Autre</option>
                        </select>
                        <button
                          type="button"
                          onClick={onSaveType}
                          disabled={savingType}
                          className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {savingType ? "Enregistrement…" : "Enregistrer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingType(false)}
                          disabled={savingType}
                          className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>

                  {(reglement.reduction_foyer ||
                    reglement.pass_sport ||
                    reglement.reduction_saison ||
                    reglement.montant_lic_assurance ||
                    reglement.montant_cotisation) && (
                    <div className="mt-2 text-xs">
                      <div className="font-medium underline">Licence-Cotisation :</div>
                      <div>Licence FFJDA : {reglement.montant_lic_assurance || 0} €</div>
                      <div>Cotisation Club : {reglement.montant_cotisation || 0} €</div>
                      <div className="mt-1 font-medium underline">Réductions :</div>
                      <div>Réduction foyer : {reglement.reduction_foyer || 0} €</div>
                      <div>Pass’Sport : {reglement.pass_sport || 0} €</div>
                      <div>Réduction saison : {reglement.reduction_saison || 0} €</div>
                    </div>
                  )}

                  <div className="mt-2 text-xs font-medium underline">Total :</div>
                  <div>
                    Montant total :{" "}
                    <span className="font-semibold">
                      {Number(reglement.montant_cotisation || 0) +
                        Number(reglement.montant_lic_assurance || 0) +
                        Number(reglement.reduction_foyer || 0) +
                        Number(reglement.pass_sport || 0) +
                        Number(reglement.reduction_saison || 0)}{" "}
                      €
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-red-500">
                    Le règlement de la licence-cotisation s'effectue sur la page Commandes & Règlements.
                  </div>
                  <div className="mt-2 text-xs text-red-500">
                    L'attestation d'inscription est téléchargeable une fois le règlement validé.
                  </div>
                </>
              ) : (
                <span className="opacity-70">Aucun règlement enregistré pour le moment.</span>
              )}
            </div>

            {/* CTA si non réglé */}
            {(!reglement || (reglement.statut_reglement || "").toLowerCase() !== "réglé") && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">Finalisez votre paiement depuis l’espace règlements.</span>
                <button
                  type="button"
                  onClick={() => navigate("/commandes-&-reglements")}
                  className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Accéder aux règlements
                </button>
              </div>
            )}
          </Section>
        </div>

        {/* Actions */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {adherent.statut_inscription === "Cours d'essai" && (
            <button
              onClick={() => setIsTrialOpen(true)}
              className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <FaThumbsUp className="mr-2" />
              Validation du cours d’essai ?
            </button>
          )}

          {/* Google Drive — seulement si un lien est présent */}
          {dossier?.lien_drive && (
            <>
              <button
                onClick={() => window.open(dossier.lien_drive, "_blank")}
                className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700"
              >
                <FaGoogleDrive className="mr-2" />
                Déposer dans Google Drive
              </button>

              <button
                onClick={async () => {
                  try {
                    const notification = `${user.prenom} ${user.nom} a déposé des documents pour ${adherent.prenom} ${adherent.nom} dans Google Drive.`;
                    await sendNotificationToAdmin(notification, user.token);
                    toast.success("Notification envoyée aux administrateurs.");
                  } catch (e) {
                    console.error(e);
                    toast.error("Erreur lors de l'envoi de la notification.");
                  }
                }}
                className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800"
              >
                <FaBell className="mr-2" />
                Signaler le dépôt
              </button>
            </>
          )}

          {/* Attestation visible uniquement si règlement validé */}
          {dossier.validate_reglement && (
            <button
              onClick={() => {
                navigate("/attestation", {
                  state: {
                    prenom: adherent.prenom,
                    nom: adherent.nom,
                    dateNaissance: new Date(adherent.date_naissance).toLocaleDateString(),
                    adresse: adherent.adresse,
                    cp: adherent.cp,
                    commune: adherent.commune,
                    cotisation: reglement?.montant_cotisation,
                    licence: reglement?.montant_lic_assurance,
                    reductionFoyer: reglement?.reduction_foyer,
                    passSport: reglement?.pass_sport,
                    reductionSaison: reglement?.reduction_saison,
                    typeReglement: reglement?.type_reglement,
                    nbReglement: reglement?.nb_reglement,
                    statutReglement: reglement?.statut_reglement,
                    observations: reglement?.observations,
                    datePaiement: new Date().toLocaleDateString(),
                    montant:
                      Number(reglement?.montant_cotisation || 0) +
                      Number(reglement?.montant_lic_assurance || 0),
                    president: settings?.responsable_club,
                    association: settings?.nom_club,
                    logo: settings?.logo_club,
                    adresse_club: settings?.adresse_club,
                    tel_club: settings?.tel_club,
                    mail_club: settings?.mail_club,
                    siteweb_club: settings?.siteweb_club,
                    siret_club: settings?.siret_club,
                    saison_active: activeSeason,
                  },
                });
              }}
              className="inline-flex items-center justify-center rounded-xl px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700"
            >
              <FaFilePdf className="mr-2" />
              Attestation d’inscription
            </button>
          )}
        </div>
      </div>

      {/* Modales */}
      <ValidateTrialModal
        isOpen={isTrialOpen}
        onClose={async (updated) => {
          setIsTrialOpen(false);
          if (updated) await handleRefreshDossier();
        }}
        adherent={adherent}
        user={user}
        currentGroupIds={
          Array.isArray(adherent?.group_ids)
            ? adherent.group_ids
            : adherent?.group_id
            ? [adherent.group_id]
            : undefined
        }
      />

      <EditDocumentsModal
        isOpen={isDocsOpen}
        onClose={() => setIsDocsOpen(false)}
        dossier={dossier}
        refreshDossiers={handleRefreshDossier}
      />
    </div>
  );
};

export default AdherentCard;
